import ResponsiveDialog from '@/components/ResponsiveDialog';
import { useChangeBookingSelection } from '@/hooks/useChangeBookingSelection';
import { TransText, type TransTextKeys } from '@/i18n/trans/text';
import { breakpoints } from '@/utils/breakpoints';
import { CONFLICT_REASON } from '@/utils/changeTrips';
import { AlertBox, Button, Icons, Typography } from '@fleet/webshop-ui';
import { useEffect, type FC } from 'react';
import { useMediaQuery } from 'react-responsive';

interface ConflictingTripsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ConflictContent {
  titleKey: TransTextKeys;
  alertKey: TransTextKeys;
  descriptionKey: TransTextKeys;
  hintKey: TransTextKeys;
}

// Record<CONFLICT_REASON, _> forces a new entry per reason — keeps copy in
// lockstep with the validator surface in `detectConflictReason`.
const CONFLICT_CONTENT: Record<CONFLICT_REASON, ConflictContent> = {
  [CONFLICT_REASON.CONFLICTING_TRIPS]: {
    titleKey: 'conflictingTrips',
    alertKey: 'conflictingTrips',
    descriptionKey: 'conflictingTripsDescription',
    hintKey: 'conflictingTripsHint',
  },
};

const ConflictingTripsModal: FC<ConflictingTripsModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { conflictReason } = useChangeBookingSelection();
  const isLaptopOrBigger = useMediaQuery({ minWidth: breakpoints.laptop });

  // Auto-close if the conflict resolves underneath us (e.g. background selection change).
  useEffect(() => {
    if (isOpen && conflictReason === null) {
      onClose();
    }
  }, [isOpen, conflictReason, onClose]);

  if (!conflictReason) return null;
  const content = CONFLICT_CONTENT[conflictReason];

  return (
    <ResponsiveDialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={<TransText i18nKey={content.titleKey} />}
      footer={
        <div className="flex w-full gap-2 laptop:flex-row laptop:justify-end">
          <Button
            type="button"
            onClick={onClose}
            data-testid="conflicting-trips-back"
            size={isLaptopOrBigger ? 'regular' : 'large'}
            className="rounded-lg laptop:w-auto"
            startAdornment={<Icons.arrowLeft />}
          >
            <TransText i18nKey="back" />
          </Button>
        </div>
      }
      content={
        <div
          className="flex flex-col gap-4 p-4"
          data-testid="conflicting-trips-modal-content"
        >
          <AlertBox
            variant="negative"
            data-testid="conflicting-trips-alert"
            text={<TransText i18nKey={content.alertKey} />}
          />
          <Typography variant="paragraph">
            <TransText i18nKey={content.descriptionKey} />
          </Typography>
          <Typography variant="body1-bold">
            <TransText i18nKey={content.hintKey} />
          </Typography>
        </div>
      }
    />
  );
};

export default ConflictingTripsModal;
